<script setup>
import Button from "@/components/ui/button/Button.vue";
import Card from "@/components/ui/card/Card.vue";
import CardContent from "@/components/ui/card/CardContent.vue";
import CardFooter from "@/components/ui/card/CardFooter.vue";
import CardHeader from "@/components/ui/card/CardHeader.vue";
import { isDarkMode } from "@/darkmode.js";
import {
    CalendarDaysIcon,
    ChevronDown,
    LaptopMinimalIcon,
    MapPinIcon,
    PencilIcon,
    X,
} from "lucide-vue-next";
import { ref } from "vue"; // Import ref for reactive variables

const emit = defineEmits(["dismiss"]); // Declare custom events

defineProps({
    name: String,
    img: {
        type: String,
    },
    date: String,
    location: String,
    description: String,
    seat: {
        type: String,
        required: false,
    },
    signedUp: Boolean,
    ongoing: String,
    id: String,
    userAuthenticated: { type: Boolean, default: false },
});

const signUpPath = ref(`/profile`); // Path to sign-in page
</script>

<template>
    <Card class="w-2xl mx-auto p-0 pb-4">
        <CardHeader
            class="p-0 w-full h-32 top-0 z-0 rounded-t-md"
            :style="{
                backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%), url('${img}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            }"
            ><Button
                variant="destructive"
                size="icon"
                class="h-8 w-8 ml-auto mr-2 mt-2"
                @click="emit('dismiss')"
            >
                <X />
            </Button>
            <div
                class="font-bold px-3 text-primary-foreground flex items-end h-20 pb-2 dark:text-primary-foreground"
            >
                {{ name }}
            </div>
        </CardHeader>
        <div class="h-full flex flex-col">
            <CardContent class="px-0 flex-shrink-0">
                <div
                    v-if="!signedUp"
                    class="border-b px-3 flex items-start py-4"
                >
                    <div class="grid grid-cols-2 gap-4 w-full text-sm">
                        <div class="flex items-center">
                            <CalendarDaysIcon class="w-4 h-4 mr-1" /> {{ date }}
                        </div>
                        <div class="flex items-center">
                            <MapPinIcon class="w-4 h-4 mr-1" /> {{ location }}
                        </div>
                        <div class="col-span-2">
                            <RouterLink
                                v-if="userAuthenticated"
                                :to="`/signup/${id}`"
                            >
                                <Button class="w-full">Sign up</Button>
                            </RouterLink>
                            <RouterLink v-else :to="signUpPath">
                                <Button class="w-full">Sign in</Button>
                            </RouterLink>
                        </div>
                    </div>
                </div>
                <div
                    v-if="signedUp"
                    class="border-b px-3 flex items-start py-2"
                >
                    <div class="grid grid-cols-2 gap-4 w-full text-sm">
                        <div class="flex items-center">
                            <CalendarDaysIcon class="w-4 h-4 mr-1" /> {{ date }}
                        </div>
                        <div class="flex items-center">
                            <MapPinIcon class="w-4 h-4 mr-1" /> {{ location }}
                        </div>
                    </div>
                </div>
                <div
                    v-if="signedUp"
                    class="border-b px-3 flex items-center py-2"
                >
                    <div class="grid grid-cols-2 gap-4 w-full text-sm">
                        <div class="flex items-center">
                            <LaptopMinimalIcon class="w-4 h-4 mr-1" /> Seat:
                            {{ seat }}
                        </div>
                        <RouterLink
                            :to="`/signup/${id}`"
                            class="flex items-center"
                        >
                            <Button size="sm" class="w-full gap-4">
                                Change
                                <PencilIcon />
                            </Button>
                        </RouterLink>
                    </div>
                </div>

                <div
                    v-if="signedUp"
                    class="border-b px-3 flex items-start py-4"
                >
                    <div class="grid grid-cols-2 gap-4 w-full text-sm">
                        <div class="col-span-2">
                            <RouterLink :to="`/steam-game/${id}`">
                                <Button class="w-full"
                                    >Find Interested Players!</Button
                                >
                            </RouterLink>
                        </div>
                    </div>
                </div>
            </CardContent>
            <CardFooter
                ref="cardFooterRef"
                class="px-3 pt-1 flex-grow overflow-y-auto flex flex-col items-start w-full lg:max-w-screen-lg overflow-y-scroll max-h-[60dvh]"
            >
                <div class="flex-1 w-full">
                    <h1 class="font-semibold">Event Information</h1>
                    <p class="text-sm flex-1 w-full whitespace-pre-wrap">
                        {{ description }}
                    </p>
                </div>
            </CardFooter>
        </div>
    </Card>
</template>
